import React, { useCallback, useEffect, useRef, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { drawChart } from '../chartUtils.js';
import Legend from '../Legend.js';

// Default settings for motif/chart preview
const defaultSettings = {
  cellSize: 12,
  yscale: 1,
  thickLineEveryX: 0,
  thickLineEveryY: 0,
  startRow: 1,
  startCol: 1,
  noStitchColor: '#e0e0e0',
  showLabels: {
    right: true,
    left: false,
    top: false,
    bottom: true
  },
  labelDirection: {
    vertical: 'down',
    horizontal: 'right'
  },
  alternatingLabels: false,
  margin: 20, // Smaller margin for preview
  showIcons: true
};

export default function ChartViewer({
  item, // generic chart object (preferred)
  motif, // legacy support
  colorway, // preferred colorway object
  colors, // fallback colors array
  title = null,
  maxWidth = 400,
  width = 300,
  height = 200
}) {
  const canvasRef = useRef(null);

  // Prefer item, fallback to motif for backward compatibility
  const chart = item || motif || {};
  const data = chart.colordata || chart.data;
  const knitdata = chart.knitdata;
  const shapes = useMemo(() => chart.shapes || [], [chart.shapes]);
  
  // Memoize settingsObj to prevent unnecessary re-renders
  const settingsObj = useMemo(() => chart.settings || {}, [chart.settings]);

  // Get unique stitch types used in the chart
  const usedStitches = useMemo(() => {
    if (!knitdata) return [];
    
    const stitchSet = new Set();
    for (let row = 0; row < knitdata.length; row++) {
      if (knitdata[row]) {
        for (let col = 0; col < knitdata[row].length; col++) {
          if (knitdata[row][col]) {
            stitchSet.add(knitdata[row][col]);
          }
        }
      }
    }
    return Array.from(stitchSet);
  }, [knitdata]);

  // Calculate dimensions
  const rows = data ? data.length : 0;
  const cols = data && data[0] ? data[0].length : 0;

  // Merge settings with defaults
  const settings = useMemo(() => {
    const baseSettings = {
      ...defaultSettings,
      ...settingsObj
    };
    // Auto-size cell size for better preview experience
    if (cols > 0) {
      const availableWidth = maxWidth - 100;
      const maxCellSize = Math.floor(availableWidth / cols);
      baseSettings.cellSize = Math.min(baseSettings.cellSize, Math.max(8, maxCellSize));
    }
    return baseSettings;
  }, [settingsObj, maxWidth, cols]);

  const onDrawChart = useCallback(() => {
    if (!canvasRef.current || !data || rows === 0 || cols === 0) return;

    // Use provided colorway, fallback to synthetic if not provided
    let effectiveColorway = colorway;
    let colorsArray = colors || chart.colors || ['#ffffff', '#d3d3d3', '#808080', '#3d3d3d'];
    if (!effectiveColorway) {
      const defaultIcons = ['circle', 'square', 'triangle', 'star', 'diamond', 'x', 'arrow', 'arrowUp', 'arrowDown', 'arrowLeft', 'chevronRight', 'chevronLeft', 'heart', 'cross', 'dot'];
      effectiveColorway = {
        chromas: colorsArray.map((color, index) => ({
          color: color,
          icon: defaultIcons[index % defaultIcons.length]
        }))
      };
    }

    // Set canvas size for preview
    const canvas = canvasRef.current;
    canvas.width = width;
    canvas.height = height;

    drawChart({
      canvasRef,
      settings,
      cols,
      rows,
      data,
      colors: colorsArray,
      currentRow: -1,
      selection: null,
      isEdit: false,
      colorway: effectiveColorway,
      knitdata,
      stitchPreview: null,
      colorPreview: null,
      shapes,
      currentShapePreview: null,
      selectedShape: null
    });
  }, [canvasRef, settings, cols, rows, data, colorway, colors, chart.colors, knitdata, shapes, width, height]);

  useEffect(() => {
    onDrawChart();
  }, [onDrawChart]);

  // Don't render if no data
  if (!data || rows === 0 || cols === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
        <Typography variant="body2">No chart data available</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2, mb: 3 }}>
      {title && (
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: 'primary.main' }}>
          {title}
        </Typography>
      )}
      <Box sx={{ 
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: 'flex-start',
        gap: 3,
        width: 'fit-content'
      }}>
        <Box sx={{ 
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 2
        }}>
          <Box sx={{ 
            overflowX: 'auto',
            maxWidth: '100%'
          }}>
            <canvas ref={canvasRef} style={{ display: 'block', border: '1px solid #e0e0e0', borderRadius: '4px', maxWidth: '100%', maxHeight: '100%' }} />
          </Box>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
            {rows} rows × {cols} columns • Cell size: {settings.cellSize}px
            {colorway && colorway.chromas && colorway.chromas.some(c => c.icon) ?
              ` • Showing ${colorway.name || 'colorway'} icons` :
              colorway ? ` • Using ${colorway.name || 'colorway'} colors` :
              settings.showIcons ? " • Default icons displayed" : ""
            }
          </Typography>
        </Box>
        {usedStitches.length > 0 && (
          <Box sx={{ flex: '0 0 auto' }}>
            <Legend usedStitches={usedStitches} />
          </Box>
        )}
      </Box>
    </Box>
  );
}
